import {
  GOOGLE_SIGNIN_NOT_CONFIGURED_MESSAGE,
  GoogleSignInConfigError,
  isGoogleSignInConfigError,
} from './googleSignInErrors';

export function isGoogleClientIdConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim());
}

const CONFIG_MESSAGE_RE =
  /not configured|is not set|missing.*(key|client|config)|client id.*(not|missing)|configuration (error|required)/i;

export function isAuthConfigurationMessage(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed) return false;
  return CONFIG_MESSAGE_RE.test(trimmed);
}

export function getAuthConfigurationToast(
  err: unknown,
): { title: string; message: string } | null {
  if (err instanceof GoogleSignInConfigError || isGoogleSignInConfigError(err as Error)) {
    return {
      title: 'Google sign-in unavailable',
      message: err instanceof Error ? err.message : GOOGLE_SIGNIN_NOT_CONFIGURED_MESSAGE,
    };
  }

  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : '';

  if (!isAuthConfigurationMessage(message)) {
    return null;
  }

  const lower = message.toLowerCase();
  if (lower.includes('google') || lower.includes('client id')) {
    return { title: 'Google sign-in unavailable', message };
  }

  return { title: 'Setup required', message };
}

/** Auth screens: configuration problems → title + body toast; everything else → single-line toast. */
export function showAuthErrorToast(
  showError: (title: string, message?: string) => void,
  err: unknown,
  fallback = 'Something went wrong. Please try again.',
): void {
  const configToast = getAuthConfigurationToast(err);
  if (configToast) {
    showError(configToast.title, configToast.message);
    return;
  }

  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : fallback;
  showError(message || fallback);
}

const GOOGLE_CONFIG_TOAST_SESSION_KEY = 'cwy-auth-google-config-toast-shown';

/** One toast per browser tab when Google client ID env is missing (login / signup). */
export function notifyMissingGoogleClientIdToast(
  showError: (title: string, message?: string) => void,
): void {
  if (isGoogleClientIdConfigured()) return;
  if (typeof sessionStorage !== 'undefined') {
    if (sessionStorage.getItem(GOOGLE_CONFIG_TOAST_SESSION_KEY)) return;
    sessionStorage.setItem(GOOGLE_CONFIG_TOAST_SESSION_KEY, '1');
  }
  showError('Google sign-in unavailable', GOOGLE_SIGNIN_NOT_CONFIGURED_MESSAGE);
}
